import { buildBodyRootMap } from "../../utils/bodyHierarchy";
import {
  generateActuatorControlName,
  generateStableActuatorNameFromUuid,
} from "../../utils/actuatorIds";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { logger } from "../../utils/logger";
import type { Equality, Joint, JointUUID } from "../../types/scene";
import { mapAffinePositionTargetToControl } from "../actuatorStartupControls";
import { getActiveActuatorControlNamingMode } from "./namingMode";
import type { Actuator, EqualityRecord, JointRecord, MappedJoint, MapperOptions } from "./types";
import { getWorldBodyRootUuid } from "./types";

/**
 * Maps actuator names to control keys for physics worker communication
 */
export class ActuatorControlMapper {
  private actuatorMap: Map<string, string> = new Map();
  private mappingErrors: string[] = [];
  private mappingWarnings: string[] = [];
  private cacheKey: string | null = null;
  private jointBodyLookup: Map<string, string> = new Map();
  private bodyRootLookup: Record<string, string> = {};
  private equalities: EqualityRecord = {};
  private joints: JointRecord = {};
  private tendonActuatorIndexMap: Map<Actuator, number> = new Map();
  private originalTendonActuatorNames: string[] = [];
  private jointMapByName: Map<string, MappedJoint> = new Map();
  private jointMapByUuid: Map<string, MappedJoint> = new Map();

  /**
   * Creates a mapping from actuator names to control keys
   * @param actuators Array of actuators from robot state
   * @param joints Array of joints from robot state (for validation)
   * @param forceRebuild Force rebuilding even if cache is valid
   */
  buildMapping(
    actuators: Actuator[],
    joints: MappedJoint[] = [],
    options: MapperOptions = {},
    forceRebuild: boolean = false
  ): void {
    // Check cache validity
    const newCacheKey = this.generateCacheKey(actuators, joints, options);
    if (!forceRebuild && this.cacheKey === newCacheKey && this.actuatorMap.size > 0) {
      return;
    }

    // Clear existing state
    this.actuatorMap.clear();
    this.mappingErrors = [];
    this.mappingWarnings = [];
    this.cacheKey = null;
    this.jointBodyLookup = new Map();
    this.bodyRootLookup = {};
    this.equalities = options.equalities || {};
    this.joints = options.joints || {};

    if (!Array.isArray(actuators) || actuators.length === 0) {
      this.mappingWarnings.push("No actuators provided to build mapping");
      return;
    }

    // Create joint UUID/name lookups for validation
    this.jointMapByName.clear();
    this.jointMapByUuid.clear();
    joints.forEach((joint) => {
      if (!joint) return;
      if (joint.name) {
        this.jointMapByName.set(joint.name, joint);
      }
      if (joint.uuid) {
        this.jointMapByUuid.set(joint.uuid, joint);
      }
    });

    // Prepare body lookup tables if available (resolve joint refs to UUIDs)
    if (options?.bodies) {
      Object.values(options.bodies).forEach((body) => {
        if (!body || !body.uuid || !Array.isArray(body.joints)) return;
        body.joints.forEach((jointRef: unknown) => {
          let jointUuid: string | null = null;
          if (typeof jointRef === "string") {
            if (this.jointMapByUuid.has(jointRef)) {
              jointUuid = jointRef;
            } else {
              // try exact name
              const byName = this.jointMapByName.get(jointRef);
              if (byName?.uuid) jointUuid = byName.uuid;
              // try sanitized variants
              if (!jointUuid && typeof jointRef === "string") {
                const tryNames = [jointRef, jointRef.replace(/_joint$/i, "")];
                for (const n of tryNames) {
                  const m = this.jointMapByName.get(n);
                  if (m?.uuid) {
                    jointUuid = m.uuid;
                    break;
                  }
                }
              }
            }
          } else if (jointRef && typeof jointRef === "object") {
            const jointObjectRef = jointRef as Partial<Joint>;
            jointUuid = jointObjectRef.uuid || null;
            if (!jointUuid && jointObjectRef.name) {
              const byName = this.jointMapByName.get(jointObjectRef.name);
              if (byName?.uuid) jointUuid = byName.uuid;
            }
          }
          if (jointUuid) {
            this.jointBodyLookup.set(jointUuid, body.uuid);
          }
        });
      });
      this.bodyRootLookup = buildBodyRootMap(options.bodies, options.worldBodies || []);
    }

    // Build tendon actuator index mapping (matching XML generation logic)
    // Get all actuators with tendons in order, matching how XML generation processes them
    const tendonActuators = actuators.filter((a) => a && a.tendon);
    this.tendonActuatorIndexMap.clear();
    this.originalTendonActuatorNames = [];
    tendonActuators.forEach((actuator, idx) => {
      this.tendonActuatorIndexMap.set(actuator, idx);
      // Build originalTendonActuatorNames array matching XML generation
      this.originalTendonActuatorNames.push(sanitizeMjcfName(actuator.name));
    });

    let mappedCount = 0;
    actuators.forEach((actuator) => {
      if (!actuator?.name) {
        this.mappingErrors.push(`Actuator missing name: ${JSON.stringify(actuator)}`);
        return;
      }

      try {
        const tendonActuatorIndex = this.tendonActuatorIndexMap.get(actuator) ?? null;
        const originalName =
          tendonActuatorIndex !== null
            ? this.originalTendonActuatorNames[tendonActuatorIndex]
            : null;
        const controlKey = this.determineControlKey(
          actuator,
          this.jointMapByName,
          this.jointMapByUuid,
          tendonActuatorIndex,
          originalName
        );
        if (actuator.uuid) {
          this.actuatorMap.set(actuator.uuid, controlKey);
        }
        this.actuatorMap.set(actuator.name, controlKey);
        mappedCount++;

        // Validate the mapping
        if (controlKey === actuator.name && !actuator.tendon) {
          this.mappingWarnings.push(
            `Actuator "${actuator.name}" mapped to itself - may indicate fallback logic used`
          );
        }
      } catch (error) {
        this.mappingErrors.push(
          `Failed to map actuator "${actuator.name}": ${error instanceof Error ? error.message : String(error)}`
        );
      }
    });

    // Update cache key on successful mapping
    if (this.mappingErrors.length === 0) {
      this.cacheKey = newCacheKey;
    }

    // Summary logging
    if (this.mappingErrors.length > 0) {
      logger.warn(
        `[ActuatorMapping] ${this.mappingErrors.length} mapping errors:`,
        this.mappingErrors
      );
    }
    if (this.mappingWarnings.length > 0) {
      logger.info(
        `[ActuatorMapping] ${this.mappingWarnings.length} mapping warnings:`,
        this.mappingWarnings
      );
    }
    logger.info(
      `[ActuatorMapping] Successfully mapped ${mappedCount}/${actuators.length} actuators`
    );
  }

  /**
   * Generates a cache key based on actuators and joints
   * @param actuators Array of actuators
   * @param joints Array of joints
   * @returns Cache key string
   */
  private generateCacheKey(
    actuators: Actuator[],
    joints: MappedJoint[] = [],
    options: MapperOptions = {}
  ): string {
    try {
      // Create a simplified representation for caching
      const actuatorKey = actuators
        .map(
          (a) =>
            `${a.uuid || ""}:${a.name || ""}:${a.type || ""}:${a.joint || ""}:${a.tendon || ""}`
        )
        .sort()
        .join("|");

      const jointKey = joints
        .map((j) => `${j.name || ""}:${j.type || ""}`)
        .sort()
        .join("|");

      const bodiesKey = options.bodies ? Object.keys(options.bodies).sort().join("|") : "nobodies";
      const worldBodiesKey = (options.worldBodies || [])
        .map((entry) => getWorldBodyRootUuid(entry))
        .sort()
        .join("|");
      return `${actuatorKey}||${jointKey}||${bodiesKey}||${worldBodiesKey}`;
    } catch {
      // If cache key generation fails, return a unique key to force rebuild
      return `error-${Date.now()}-${Math.random()}`;
    }
  }

  /**
   * Gets the control key for a given actuator name
   * @param actuatorName The actuator name from robot state
   * @returns The control key for physics worker, or the original name if not mapped
   */
  getControlKey(actuatorName: string): string {
    return this.actuatorMap.get(actuatorName) || actuatorName;
  }

  /**
   * Creates a control payload from actuator name -> value mapping
   * @param actuatorValues Object mapping actuator names to values
   * @param actuators Array of actuators (for fallback logic)
   * @returns Object mapping control keys to values
   */
  createControlPayload(
    actuatorValues: Record<string, number>,
    actuators: Actuator[] = []
  ): Record<string, number> {
    const payload: Record<string, number> = {};

    // Create actuator name to actuator mapping for fallback
    const actuatorLookup = new Map<string, Actuator>();
    actuators.forEach((actuator) => {
      if (!actuator) return;
      if (actuator.uuid) {
        actuatorLookup.set(actuator.uuid, actuator);
      }
      if (actuator.name) {
        actuatorLookup.set(actuator.name, actuator);
      }
    });

    Object.entries(actuatorValues).forEach(([actuatorKey, value]) => {
      // Check if the input key already matches a control key in the mapping
      // (i.e., it's already in the format expected by the physics worker)
      const controlKeys = Array.from(this.actuatorMap.values());
      const isAlreadyControlKey = controlKeys.includes(actuatorKey);

      if (isAlreadyControlKey) {
        // Input key is already a valid control key that matches the physics XML, use it directly
        payload[actuatorKey] = value;
        return;
      }

      // Try to map from scene store actuator name to physics XML control key
      const controlKey = this.getControlKey(actuatorKey);

      // If mapping found a different key, use it
      if (controlKey !== actuatorKey) {
        payload[controlKey] = value;
        return;
      }

      // If no mapping found, try to find the actuator and generate a control key
      let actuator =
        actuatorLookup.get(actuatorKey) ||
        (typeof actuatorKey === "string"
          ? actuatorLookup.get(actuatorKey.replace(/__.+$/, ""))
          : undefined);
      if (!actuator && typeof actuatorKey === "string" && actuatorKey.startsWith("act_")) {
        const possibleUuid = actuatorKey.slice(4);
        if (possibleUuid) {
          actuator = actuatorLookup.get(possibleUuid);
        }
      }
      if (!actuator && typeof actuatorKey === "string" && actuatorKey.includes("__")) {
        const base = actuatorKey.split("__")[0];
        actuator = actuatorLookup.get(base);
      }

      if (actuator?.tendon) {
        let v = value;
        let ctrlMax = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[1] : 255; // default fallback for common gripper mapping
        // If ctrlMax looks like a small angular range (<= 10), prefer 255 for tendon actuators
        if (!(typeof ctrlMax === "number" && isFinite(ctrlMax) && ctrlMax > 10)) {
          ctrlMax = 255;
        }
        if (
          typeof v === "number" &&
          v >= 0 &&
          v <= 1 &&
          typeof ctrlMax === "number" &&
          isFinite(ctrlMax) &&
          ctrlMax > 1
        ) {
          v = v * ctrlMax;
        }
        // For tendon actuators, generate control key using fallback
        // Use stored index and name if available, and use stored joint maps
        const fallbackTendonIndex = this.tendonActuatorIndexMap.get(actuator) ?? null;
        const fallbackOriginalName =
          fallbackTendonIndex !== null
            ? this.originalTendonActuatorNames[fallbackTendonIndex]
            : null;
        const fallbackKey = this.determineControlKey(
          actuator,
          this.jointMapByName,
          this.jointMapByUuid,
          fallbackTendonIndex,
          fallbackOriginalName
        );
        payload[fallbackKey] = v;
        return;
      }

      // For non-tendon actuators, try fallback logic to generate control key
      if (actuator) {
        const fallbackTendonIndex = this.tendonActuatorIndexMap.get(actuator) ?? null;
        const fallbackOriginalName =
          fallbackTendonIndex !== null
            ? this.originalTendonActuatorNames[fallbackTendonIndex]
            : null;
        const fallbackKey = this.determineControlKey(
          actuator,
          this.jointMapByName,
          this.jointMapByUuid,
          fallbackTendonIndex,
          fallbackOriginalName
        );
        payload[fallbackKey] = value;
        return;
      }

      // Last resort: use the original key (might not work, but better than nothing)
      payload[actuatorKey] = value;
    });

    return payload;
  }

  /**
   * Creates a control payload from user-facing position targets.
   *
   * The UI stores joint-space targets for position-like actuators. MuJoCo `<general>`
   * affine servos may require a different raw ctrl value to represent that same target:
   * force = gainprm[0] * ctrl + biasprm[1] * q + biasprm[2] * qvel.
   */
  createPositionControlPayload(
    actuatorPositionTargets: Record<string, number>,
    actuators: Actuator[] = []
  ): Record<string, number> {
    const mappedTargets: Record<string, number> = {};

    Object.entries(actuatorPositionTargets).forEach(([actuatorKey, value]) => {
      const actuator = this.findActuatorForControlInput(actuatorKey, actuators);
      mappedTargets[actuatorKey] =
        actuator && this.isAffineGeneralJointPositionServo(actuator)
          ? mapAffinePositionTargetToControl(value, actuator.gainprm, actuator.biasprm)
          : value;
    });

    return this.createControlPayload(mappedTargets, actuators);
  }

  private findActuatorForControlInput(actuatorKey: string, actuators: Actuator[]): Actuator | null {
    const key = String(actuatorKey || "");
    if (!key) return null;

    for (const actuator of actuators) {
      if (!actuator) continue;
      const candidates = new Set<string>();
      if (actuator.uuid) {
        const uuid = String(actuator.uuid);
        candidates.add(uuid);
        candidates.add(generateStableActuatorNameFromUuid(uuid));
        candidates.add(`act_${uuid}`);
      }
      if (actuator.name) candidates.add(String(actuator.name));

      try {
        candidates.add(this.getCompiledXmlControlKey(actuator));
      } catch {
        // Mapping can still proceed by UUID/name candidates.
      }

      if (candidates.has(key)) return actuator;

      if (key.includes("__")) {
        const base = key.split("__")[0];
        if (candidates.has(base)) return actuator;
      }
      if (key.startsWith("act_") && actuator.uuid && key.slice(4) === String(actuator.uuid)) {
        return actuator;
      }
    }

    return null;
  }

  private isAffineGeneralJointPositionServo(actuator: Actuator): boolean {
    const kind = String(actuator?.kind || actuator?.type || "").toLowerCase();
    return (
      kind === "general" &&
      !!actuator?.joint &&
      !actuator?.tendon &&
      Array.isArray(actuator.gainprm) &&
      Array.isArray(actuator.biasprm)
    );
  }

  /**
   * MJCF / compiled-model actuator name used by the physics worker (`sim.ctrl` row),
   * matching {@link createControlPayload} destination keys.
   */
  getCompiledXmlControlKey(actuator: Actuator): string {
    const tendonActuatorIndex = this.tendonActuatorIndexMap.get(actuator) ?? null;
    const originalName =
      tendonActuatorIndex !== null ? this.originalTendonActuatorNames[tendonActuatorIndex] : null;
    return this.determineControlKey(
      actuator,
      this.jointMapByName,
      this.jointMapByUuid,
      tendonActuatorIndex,
      originalName
    );
  }

  /**
   * Determines the control key for an actuator
   * @param actuator The actuator object
   * @param jointMap Map of joint names to joint objects for validation
   * @returns The control key to use
   */
  private determineControlKey(
    actuator: Actuator,
    jointMapByName: Map<string, MappedJoint>,
    jointMapByUuid: Map<string, MappedJoint>,
    tendonActuatorIndex: number | null = null,
    originalTendonActuatorName: string | null = null
  ): string {
    // Most-stable mapping: when generated XML uses UUID actuator names, key by uuid.
    if (actuator?.uuid && getActiveActuatorControlNamingMode() === "stableUuid") {
      return generateStableActuatorNameFromUuid(actuator.uuid);
    }
    // Verbatim XML commonly keeps original actuator names; prefer those in scene-name mode.
    if (actuator?.name && getActiveActuatorControlNamingMode() === "sceneName") {
      return actuator.name;
    }
    const jointLookupKey = actuator.joint;
    let jointRef: MappedJoint | null = null;
    let jointUuid: string | null = null;
    if (jointLookupKey && jointMapByUuid.has(jointLookupKey)) {
      jointRef = jointMapByUuid.get(jointLookupKey) ?? null;
      jointUuid = jointRef?.uuid || null;
    } else if (typeof jointLookupKey === "string" && jointMapByName.has(jointLookupKey)) {
      jointRef = jointMapByName.get(jointLookupKey) ?? null;
      jointUuid = jointRef?.uuid || null;
    } else if (typeof jointLookupKey === "string") {
      // Try common name variants
      const nameCandidates = [jointLookupKey, jointLookupKey.replace(/_joint$/i, "")];
      for (const n of nameCandidates) {
        if (jointMapByName.has(n)) {
          const j = jointMapByName.get(n);
          if (j?.uuid) {
            jointRef = j;
            jointUuid = j.uuid;
            break;
          }
        }
      }
      // If still not found, do not treat name as UUID
      if (!jointUuid) {
        jointUuid = null;
      }
    }

    // For tendon actuators, use the same baseName logic as XML generation:
    // originalActuator?.name || originalName
    // In XML generation, originalActuator is found by matching, but we have the actuator directly
    // So we use actuator.name (raw) first, matching originalActuator?.name
    // Then fall back to originalName (sanitized) if needed
    const preferredBase = actuator.tendon
      ? actuator.name ||
        originalTendonActuatorName ||
        `gripper_${tendonActuatorIndex !== null ? tendonActuatorIndex + 1 : 1}`
      : actuator.name ||
        (jointRef?.name ? jointRef.name.replace(/_joint$/i, "") : undefined) ||
        (typeof jointLookupKey === "string" ? jointLookupKey.replace(/_joint$/i, "") : undefined) ||
        actuator.tendon ||
        "actuator";

    // For tendon actuators, look up joints from equality constraints if jointUuid is not found
    // Match the same way XML generation does: by processing equality constraints in order
    // and matching to actuators with tendons by index
    let tendonRootUuid: string | null = null;
    if (
      !jointUuid &&
      actuator.tendon &&
      Object.keys(this.equalities).length > 0 &&
      Object.keys(this.joints).length > 0
    ) {
      // Build the same tendon pairs logic as XML generation
      const equalityList = Object.values(this.equalities);
      const jointMap = this.joints;

      // Find equality constraints of type "joint" in order (matching XML generation)
      // XML generation processes them with forEach((eq, idx)), so we need to match by index
      const jointEqualityConstraints: Array<{
        eq: Equality;
        j1Uuid: JointUUID;
        j2Uuid: JointUUID;
        idx: number;
      }> = [];
      equalityList.forEach((eq, idx) => {
        if (eq && eq.type === "joint" && (eq.objtype === "joint" || !eq.objtype)) {
          const j1Uuid = eq.obj1 as JointUUID | undefined;
          const j2Uuid = eq.obj2 as JointUUID | undefined;
          if (j1Uuid && j2Uuid && jointMap[j1Uuid] && jointMap[j2Uuid]) {
            // Check if joints are valid 1-DoF pairs (matching XML generation filter)
            const j1 = jointMap[j1Uuid];
            const j2 = jointMap[j2Uuid];
            const kind1 = String(j1?.kind || "").toLowerCase();
            const kind2 = String(j2?.kind || "").toLowerCase();
            const dof1Ok = kind1 === "slide" || kind1 === "hinge" || kind1 === "";
            const dof2Ok = kind2 === "slide" || kind2 === "hinge" || kind2 === "";
            if (dof1Ok && dof2Ok) {
              jointEqualityConstraints.push({ eq, j1Uuid, j2Uuid, idx });
            }
          }
        }
      });

      // Match by index: equality constraint at index N matches tendon actuator at index N
      // If tendonActuatorIndex is null, use the first matching constraint (for cases where index isn't available)
      const eqIndex =
        tendonActuatorIndex !== null && tendonActuatorIndex < jointEqualityConstraints.length
          ? tendonActuatorIndex
          : jointEqualityConstraints.length > 0
            ? 0
            : -1;

      if (eqIndex >= 0) {
        const matchedEq = jointEqualityConstraints[eqIndex];
        // Build joint info for both joints to match XML generation logic exactly
        let joint1RootUuid: string | null = null;
        let joint2RootUuid: string | null = null;

        if (matchedEq.j1Uuid && jointMapByUuid.has(matchedEq.j1Uuid)) {
          const j1HostBodyUuid = this.jointBodyLookup.get(matchedEq.j1Uuid) || null;
          joint1RootUuid =
            j1HostBodyUuid && Object.keys(this.bodyRootLookup).length > 0
              ? this.bodyRootLookup[j1HostBodyUuid] || j1HostBodyUuid
              : j1HostBodyUuid;
        }

        if (matchedEq.j2Uuid && jointMapByUuid.has(matchedEq.j2Uuid)) {
          const j2HostBodyUuid = this.jointBodyLookup.get(matchedEq.j2Uuid) || null;
          joint2RootUuid =
            j2HostBodyUuid && Object.keys(this.bodyRootLookup).length > 0
              ? this.bodyRootLookup[j2HostBodyUuid] || j2HostBodyUuid
              : j2HostBodyUuid;
        }

        // Use joint1 first, then joint2 (matching XML generation: joint1Uuid || joint2Uuid)
        if (matchedEq.j1Uuid && jointMapByUuid.has(matchedEq.j1Uuid)) {
          jointRef = jointMapByUuid.get(matchedEq.j1Uuid) ?? null;
          jointUuid = matchedEq.j1Uuid;
        } else if (matchedEq.j2Uuid && jointMapByUuid.has(matchedEq.j2Uuid)) {
          jointRef = jointMapByUuid.get(matchedEq.j2Uuid) ?? null;
          jointUuid = matchedEq.j2Uuid;
        }

        // Use rootUuid from joint1 first, then joint2 (matching XML: joint1Info?.rootUuid || joint2Info?.rootUuid)
        tendonRootUuid = joint1RootUuid || joint2RootUuid;
      }
    }

    const hostBodyUuid = jointUuid ? this.jointBodyLookup.get(jointUuid) || null : null;
    // For tendon actuators, use the precomputed tendonRootUuid (matching XML generation)
    // For regular actuators, compute from hostBodyUuid
    const rootUuid =
      actuator.tendon && tendonRootUuid !== null
        ? tendonRootUuid
        : hostBodyUuid && Object.keys(this.bodyRootLookup).length > 0
          ? this.bodyRootLookup[hostBodyUuid] || hostBodyUuid
          : hostBodyUuid;

    // Use raw tendon name (not sanitized) to match XML generation which uses originalActuator?.tendon
    return generateActuatorControlName({
      baseName: preferredBase,
      jointUuid: jointUuid || jointRef?.uuid || null,
      rootBodyUuid: rootUuid || null,
      tendonName: actuator.tendon || null,
    });
  }

  /**
   * Gets all mapped control keys
   * @returns Array of control keys
   */
  getControlKeys(): string[] {
    return Array.from(this.actuatorMap.values());
  }

  /**
   * Gets the mapping for debugging
   * @returns Object showing actuator name -> control key mapping
   */
  getMapping(): Record<string, string> {
    const mapping: Record<string, string> = {};
    this.actuatorMap.forEach((controlKey, actuatorName) => {
      mapping[actuatorName] = controlKey;
    });
    return mapping;
  }

  /**
   * Gets errors that occurred during mapping
   * @returns Array of error messages
   */
  getErrors(): string[] {
    return [...this.mappingErrors];
  }

  /**
   * Gets warnings that occurred during mapping
   * @returns Array of warning messages
   */
  getWarnings(): string[] {
    return [...this.mappingWarnings];
  }

  /**
   * Checks if the mapping has errors
   * @returns True if there are mapping errors
   */
  hasErrors(): boolean {
    return this.mappingErrors.length > 0;
  }

  /**
   * Checks if the mapping has warnings
   * @returns True if there are mapping warnings
   */
  hasWarnings(): boolean {
    return this.mappingWarnings.length > 0;
  }

  /**
   * Forces a rebuild of the mapping on next buildMapping call
   */
  invalidateCache(): void {
    this.cacheKey = null;
  }

  /**
   * Checks if the mapping is using cached data
   * @returns True if current mapping came from cache
   */
  isUsingCache(): boolean {
    return this.cacheKey !== null && this.actuatorMap.size > 0;
  }
}
